import React from 'react'
import { Separator, Card, CardContent, CardHeader, Modal, Button } from '@/shared/ui'
import { Thermometer } from '@/modules/termometro'
import ThermometerWithGate from '@/modules/termometro/components/ThermometerWithGate'
import { fetchThermometerById } from '@/modules/termometro/services/thermometerService'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { getThermometerMemberVisible } from '@/modules/settings/services/thermometerFeatureService'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { useOportunidadeDetails } from '../hooks/useOportunidadeDetails'
import NearBreakCard from './NearBreakCard'
import MandateWarningCard from './MandateWarningCard'
import PublishedAtInfo from './PublishedAtInfo'
import { SignalsMultiTfWarningCard } from './SignalsMultiTfWarningCard'
// Removido card antigo de conflu�ncias com barra de progresso
import { buildContextParts, tfLabelPT, buildInvalidationText, extractFiboLevels, signalTfForTarget } from '../utils/contextText'
import { deriveFiboForOpportunity } from '../utils/fibo'
import { fetchCandles } from '@/modules/graph-view/services/candlesService'
import { X, HelpCircle } from 'lucide-react'
import { getThermometerMemberTip, DEFAULT_THERMOMETER_MEMBER_TIP } from '@/modules/termometro/services/thermometerTextService'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { useLastPrice } from '@/modules/signals/hooks/useLastPrice'
import {
  fetchSignalsMultiTfState,
  fetchSignalsMultiTfDirectionConflicts,
  type OpportunityMultiTfState,
  type OpportunityMultiTfDirectionConflict,
} from '@/modules/signals'
import { shouldShowMultiTfCriticalWarning } from '@/modules/signals/utils/multiTfWarning'

export default function OportunidadeModalRight({ showHeader = true }: { showHeader?: boolean }) {
  const { item, closeModal } = useOportunidadeModal()
  const { data, asset, loading, error } = useOportunidadeDetails(item)

  return (
    <div className="space-y-3">
      {showHeader && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {asset?.logo_url ? (
              <img src={asset.logo_url} alt={item?.symbol || ''} className="h-10 w-10 rounded-full object-cover" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-muted" />
            )}
            <div>
              <div className="text-sm font-semibold">{asset?.name || item?.name || item?.symbol}</div>
              <div className="text-xs text-foreground/70">{item?.symbol}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={closeModal}
            aria-label="Fechar"
            className="inline-flex h-10 w-10 items-center justify-center rounded hover:bg-muted/30 hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
	      )}

	      <SignalsMultiTfWarningCard data={data} />
	      <TopSummary data={data} symbol={(data as any)?.symbol || item?.symbol} />
      {/* Card antigo de conflu�ncias removido (usar apenas o term�metro) */}
      <Separator />

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>
      )}
      {loading && <div className="text-sm text-foreground/70">Carregando detalhes...</div>}

      {!loading && data && (
        <div className="space-y-3">
          <MandateWarningCard data={data} />
          <DadosTecnicosCard data={data} />
          <ThermometerConfluencesCard data={data} />
          <NearBreakCard data={data} />
        </div>
      )}
    </div>
  )
}

function tfShort(tfRaw?: string | null): string { return tfLabelPT(tfRaw) || '-' }
function minorForMajor(tfRaw?: string | null): string | null {
  const tf = String(tfRaw || '').toUpperCase()
  if (!tf) return null
  // Cadeia oficial: M ? W ? D ? 720 ? 240 ? 60 (1h sem "filho")
  if (tf === 'M') return 'W'
  if (tf === 'W') return 'D'
  if (tf === 'D') return '720'
  if (tf === '720') return '240'
  if (tf === '240') return '60'
  if (tf === '60') return null
  if (tf === '30') return '15'
  if (tf === '15') return '5'
  if (tf === '5') return null
  return null
}

function MultiTfWarningCard({ data }: { data: any }) {
  const idNum = Number((data as any)?.id)
  const symbol = String((data as any)?.symbol || '').toUpperCase()

  if (!Number.isFinite(idNum) || !symbol) return null

  const signalsEnabled = useSignalsEnabled()
  const isSignalsOn = signalsEnabled === true

	  const { signals } = useOpportunitySignals(idNum, isSignalsOn)
	  const lastPrice = useLastPrice(symbol)
	
	  const [multiState, setMultiState] = React.useState<OpportunityMultiTfState | null>(null)
	  const [multiDirection, setMultiDirection] =
	    React.useState<OpportunityMultiTfDirectionConflict | null>(null)
	
  React.useEffect(() => {
    if (!isSignalsOn || !Number.isFinite(idNum)) {
      setMultiState(null)
      setMultiDirection(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const [stateRes, conflictRes] = await Promise.all([
          fetchSignalsMultiTfState([idNum]),
          fetchSignalsMultiTfDirectionConflicts([idNum]),
        ])
        if (cancelled) return
        setMultiState(stateRes[String(idNum)] ?? null)
        setMultiDirection(conflictRes[String(idNum)] ?? null)
      } catch {
        if (!cancelled) {
          setMultiState(null)
          setMultiDirection(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [idNum, isSignalsOn])

  const showWarning = (() => {
    if (!isSignalsOn || !Number.isFinite(idNum)) return false
    const state = multiState?.state
    const parentTf = multiState?.parentTf
    if (!parentTf) return false
    const price = typeof lastPrice === 'number' ? lastPrice : null
    return shouldShowMultiTfCriticalWarning({
      multiTfState: state,
      signals: signals ?? null,
      lastPrice: price,
    })
  })()

  if (!showWarning || !multiState?.parentTf) return null

  const tfAtualLabel =
    tfLabelPT((data as any)?.target_timeframe) ||
    tfLabelPT((data as any)?.timeframe) ||
    '-'
  const tfMaiorLabel = tfLabelPT(multiState.parentTf) || multiState.parentTf

  return (
    <Card className="border-amber-400/40 bg-amber-500/10">
      <CardHeader className="pb-1 text-sm font-semibold text-amber-300">
        Oportunidade em zona cr�tica.
      </CardHeader>
      <CardContent className="pt-0 text-xs text-amber-100">
        Essa oportunidade em {tfAtualLabel} aumentou as chances de atingir a zona de stop e iniciar uma corre��o no {tfMaiorLabel}.
      </CardContent>
    </Card>
  )
}

function TopSummary(props: { data: any; symbol?: string | null }) {
  const { data, symbol } = props
  if (!data) return null
  const cf = (data as any)?.confluence_factors || {}

  const trend = String((data as any)?.target_trend ?? cf.trend_alvo ?? '').toLowerCase()
  const trendPt = trend === 'bearish' || trend === 'baixa' ? 'baixa' : trend ? 'alta' : ''
  const pivotRaw = String(cf.pivot_type ?? '').toLowerCase()
  let pivotLabel = ''
  if (pivotRaw.includes('ascending') || pivotRaw.includes('pivot_low') || (trendPt === 'alta' && !pivotRaw)) pivotLabel = 'fundo ascendente'
  if (pivotRaw.includes('descending') || pivotRaw.includes('pivot_high') || (trendPt === 'baixa' && !pivotRaw)) pivotLabel = 'topo descendente'
  const tfTarget = tfLabelPT((data as any)?.target_timeframe) || tfLabelPT(cf.target_timeframe_raw)
  const parts = [
    String(symbol || (data as any)?.symbol || '').toUpperCase(),
    trendPt ? `- Tend\u00eancia de ${trendPt}:` : '',
    pivotLabel && tfTarget ? `buscando ${pivotLabel} no ${tfTarget}` : '',
  ].filter(Boolean)
  const fallbackTitle = parts.join(' ')
  const title = fallbackTitle || '-'

  // Conte�dos legados podem conter frases de RSI no formato antigo.
  // Sanitizamos: ocultar qualquer texto que mencione explicitamente "RSI" (regra antiga).
  const legacySummary = (cf.card_summary || cf.summary || '') as string
  const legacyDescription = ((data as any)?.opportunity_description || cf.opportunity_description || cf.description || '') as string
  const summary = legacySummary && !/\bRSI\b/i.test(legacySummary) ? legacySummary : null
  const description = legacyDescription && !/\bRSI\b/i.test(legacyDescription) ? legacyDescription : null

  return (
    <Card>
      <CardHeader className="text-base font-semibold">{String(title)}</CardHeader>
      <CardContent className="space-y-3">
        {summary && <div className="text-sm text-foreground/80">{String(summary)}</div>}
        {description && <div className="text-sm text-foreground/70">{String(description)}</div>}
        <ContextoInlineNew data={data} />
      </CardContent>
    </Card>
  )
}

function DadosTecnicosCard({ data }: { data: any }) {
  const cf = (data as any)?.confluence_factors || (data as any)?.confluence || {}
  let pivotType: string = cf.pivot_type ?? cf.pivotType ?? '-'
  if (typeof pivotType === 'string') {
    const t = pivotType.toLowerCase()
    if (t.includes('ascending_bottom') || t.includes('pivot_low')) pivotType = 'fundo ascendente'
    if (t.includes('descending_top') || t.includes('pivot_high')) pivotType = 'topo descendente'
  }
  const trendAlvo = (data as any)?.target_trend ?? '-'
  // SSOT: alvo vem da pr�pria oportunidade; cf.* apenas como �ltimo fallback
  const targetTimeframeRaw = (data as any)?.target_timeframe ?? cf.target_timeframe_raw ?? (data as any)?.maior_timeframe ?? '-'
  // Timeframe (sinal) = filho imediato do alvo; se inexistente (1h), mostrar '-' ou o TF de cria��o como fallback informativo
  const fallbackSignal = (data as any)?.timeframe || (data as any)?.timeframe_raw || cf.timeframe_raw || null
  const timeframeRaw = signalTfForTarget(String(targetTimeframeRaw)) || String(fallbackSignal || '-')
  const fiboBase = extractFiboLevels(data as any)
  const [fibo, setFibo] = React.useState<{ f382?: number|null; f50?: number|null; f618?: number|null }>(fiboBase)
  const trendRaw = String((data as any)?.target_trend ?? cf?.trend_alvo ?? '').toLowerCase().trim()
  const isShort = (trendRaw === 'bearish' || trendRaw === 'baixa' || trendRaw === 'short')
  const fiboView = React.useMemo(() => {
    if (!isShort) return fibo

    const f382 = fibo.f382 ?? null
    const f50 = fibo.f50 ?? null
    const f618 = fibo.f618 ?? null

    // O SSOT do Supabase retorna `fibo_levels` ja orientado para bearish (0.382 < 0.618),
    // mas alguns fallbacks no front ainda retornam a versao "long-style" (0.382 > 0.618).
    // Para manter o card coerente com o grafico, so invertemos quando vier long-style.
    if (f382 != null && f618 != null && Number.isFinite(Number(f382)) && Number.isFinite(Number(f618)) && Number(f382) < Number(f618)) {
      return { f382, f50, f618 }
    }

    return { f382: f618, f50, f618: f382 }
  }, [isShort, fibo])

  React.useEffect(() => {
    setFibo(fiboBase)
    let cancel = false
    deriveFiboForOpportunity(data as any, { limit: 300, k: 2 })
      .then((res) => { if (!cancel && res) setFibo({ f382: res.f382 ?? null, f50: res.f50 ?? null, f618: res.f618 ?? null }) })
      .catch(()=>{})
    return () => { cancel = true }
  }, [ (data as any)?.id, (data as any)?.published_at, targetTimeframeRaw ])
  const fmt = (n: any) => {
    const v = Number(n)
    if (!Number.isFinite(v)) return '-'
    return v.toLocaleString('en-US', { maximumFractionDigits: 6 })
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Dados técnicos</CardHeader>
      <CardContent>
        <div className="text-[10px] text-foreground/60 mb-2">Horario baseado no timezone do usuario</div>
        <div className="grid grid-cols-2 gap-2 text-xs text-foreground/80">
          <span>Tendência do alvo</span>
          <span className="text-right"><TrendColored value={String(trendAlvo)} /></span>
          <span>Timeframe (sinal)</span><span className="text-right">{tfShort(timeframeRaw)}</span>
          <span>Timeframe alvo</span><span className="text-right">{tfShort(targetTimeframeRaw)}</span>
          <PublishedAtInfo publishedAt={(data as any)?.published_at} />
          {(fiboView.f382 != null || fiboView.f50 != null || fiboView.f618 != null) && (
            <>
              <span>Fibo 0.382</span><span className="text-right">{fiboView.f382 != null ? fmt(fiboView.f382) : '-'}</span>
              <span>Fibo 0.5</span><span className="text-right">{fiboView.f50 != null ? fmt(fiboView.f50) : '-'}</span>
              <span>Fibo 0.618</span><span className="text-right">{fiboView.f618 != null ? fmt(fiboView.f618) : '-'}</span>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ThermometerConfluencesCard({ data }: { data: any }) {
  const [row, setRow] = React.useState<any | null>(null)
  const [tipModalOpen, setTipModalOpen] = React.useState(false)
  const [tipText, setTipText] = React.useState('')
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const [memberVisible, setMemberVisible] = React.useState<boolean | null>(null)
  React.useEffect(() => {
    let mounted = true
    getThermometerMemberVisible()
      .then((on)=> { if (mounted) setMemberVisible(!!on) })
      .catch(()=> { if (mounted) setMemberVisible(false) })
    return () => { mounted = false }
  }, [])
  const thermoAllowed = isMaster ? true : !!memberVisible
  const masterNote = isMaster && memberVisible === false ? 'Recurso habilitado somente para usuario master' : undefined
  React.useEffect(() => {
    let mounted = true
    getThermometerMemberTip().then((txt)=> { if (mounted) setTipText(String(txt||"")) }).catch(()=>{})
    return () => { mounted = false }
  }, [])
  React.useEffect(() => {
    let cancel = false
    const idNum = Number((data as any)?.id)
    if (!Number.isFinite(idNum)) return
    fetchThermometerById(idNum).then((r) => { if (!cancel) setRow(r) }).catch(()=>{})
    // Realtime: atualiza quando a linha for criada/atualizada
    const channel = supabase
      .channel(`thermo_modal_${idNum}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'opportunity_thermometer', filter: `opportunity_id=eq.${idNum}` }, (payload: any) => {
        const rec = payload?.new || payload?.old
        if (!rec) return
        setRow({
          opportunity_id: String(rec.opportunity_id),
          symbol: String(rec.symbol || '').toUpperCase(),
          percent: Math.round(Math.max(0, Math.min(100, Number(rec.percent || 0)))),
          updated_at: String(rec.updated_at || new Date().toISOString()),
          breakdown: (rec as any).breakdown || null,
        })
      })
      .subscribe()
    return () => { cancel = true; try { supabase.removeChannel(channel) } catch {} }
  }, [data?.id])

  const cf = (data as any)?.confluence_factors || {}
  const trend = String((data as any)?.target_trend ?? cf.trend_alvo ?? '').toLowerCase()
  const objective = trend === 'bearish' || trend === 'baixa' ? 'Short' : 'Long'
  const b = row?.breakdown || {}

  // Regras simples para classificar conflu�ncias do breakdown do term�metro
  const positives: string[] = []
  const negatives: string[] = []

  const push = (cond: boolean, pos: string, neg: string) => { cond ? positives.push(pos) : negatives.push(neg) }

  // Labels amig�veis de TF (usando breakdown quando dispon�vel)
  const tfMinorLbl = tfLabelPT(b?.tf?.minor || signalTfForTarget((data as any)?.target_timeframe || (data as any)?.target_timeframe_raw) || (data as any)?.timeframe_raw || (data as any)?.timeframe) || 'TF menor'
  const tfMediumLbl = tfLabelPT(b?.tf?.medium) || 'TF direto abaixo'
  const tfMajorLbl = tfLabelPT(b?.tf?.major || (data as any)?.target_timeframe_raw || (data as any)?.target_timeframe) || 'TF alvo'

  if (b.emaScore != null) {
    push(
      Number(b.emaScore) >= 0.6,
      'EMAs coerentes com o contexto',
      'EMAs sinalizam resistencia/contramao',
    )
  }
  if (b.emaAdjust?.minor12 != null) {
    push(
      Number(b.emaAdjust.minor12) > 1,
      `Reacao favoravel na EMA 12 do ${tfMinorLbl}`,
      `Reacao desfavoravel na EMA 12 do ${tfMinorLbl}`,
    )
  }
  if (b.emaAdjust?.minor26 != null) {
    push(
      Number(b.emaAdjust.minor26) > 1,
      `Reacao favoravel na EMA 26 do ${tfMinorLbl}`,
      `Reacao desfavoravel na EMA 26 do ${tfMinorLbl}`,
    )
  }

  if (b.rsiScore != null) {
    push(
      Number(b.rsiScore) >= 0.6,
      `RSI do ${tfMinorLbl} favoravel ao trade`,
      `RSI do ${tfMinorLbl} desfavoravel ao trade`,
    )
  }
  if (b.rsiAdjust != null) {
    push(
      Number(b.rsiAdjust) > 1,
      `RSI do ${tfMinorLbl} alinhado ao movimento do preco`,
      `RSI do ${tfMinorLbl} divergindo do movimento do preco`,
    )
  }

  if (b.fiboScore != null) {
    push(
      Number(b.fiboScore) >= 0.5,
      'Preco na zona de Fibo (0.382/0.5/0.618)',
      'Preco longe das zonas de Fibo',
    )
  }
  if (b.volumeScore != null) {
    push(
      Number(b.volumeScore) >= 0.6,
      `Volume decrescente na correcao (${tfMajorLbl})`,
      `Volume crescente durante a correcao (${tfMajorLbl})`,
    )
  }

  // Deduplicar labels caso medium e minor coincidam (ex.: alvo Di�rio)
  const seen = new Set<string>()
  function addPivot(val: any, lbl: string){
    if (typeof val !== 'number' || !lbl) return
    const key = `${lbl}:${val>0?'+':'-'}`
    if (seen.has(key)) return
    seen.add(key)
    if (val > 0) positives.push(`Piv� de ${lbl} a favor`)
    else if (val < 0) negatives.push(`Piv� de ${lbl} contra`)
  }
  // Exibir piv� apenas do TF menor sequencial (regra oficial)
  addPivot(b.pivot?.minor, tfMinorLbl)
  // N�o exibimos piv� do TF alvo (maior) para evitar confus�o durante a corre��o

  const percent = typeof row?.percent === 'number' ? row?.percent : null

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Confluências do Termômetro 
        <button
            type="button"
            onClick={() => setTipModalOpen(true)}
            className="group relative inline-flex items-center text-white hover:text-emerald-400 ml-2 float-right"
            aria-label="Ajuda do term�metro"
            style={{ marginRight: '1px' }}
          >
             <HelpCircle className="h-4 w-4" />
          </button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-end">
          
        </div>
        {!row ? (
          <div className="text-sm text-foreground/70">
            Aguardando cálculo do termômetro (até 5 min).
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <div className="mb-1 text-sm font-medium text-emerald-400">Confluências positivas</div>
              <ul className="list-disc pl-4 text-sm text-foreground/80 space-y-1">
                {(positives.length ? positives : ['-']).map((t, i) => (<li key={i}>{t}</li>))}
              </ul>
            </div>
            <div>
              <div className="mb-1 text-sm font-medium text-red-400">Confluências negativas</div>
              <ul className="list-disc pl-4 text-sm text-foreground/80 space-y-1">
                {(negatives.length ? negatives : ['-']).map((t, i) => (<li key={i}>{t}</li>))}
              </ul>
            </div>
          </div>
        )}
          <div className="mt-2 flex items-center gap-2">
            <div className="flex-1">
              <ThermometerWithGate enabled={thermoAllowed} value={percent ?? 0} note={masterNote} />
            </div>
            {thermoAllowed && (
              <span className="text-xs text-foreground/80">{percent != null ? `${percent}%` : '-'}</span>
            )}
          </div>
          <Modal open={tipModalOpen} onOpenChange={setTipModalOpen} className="max-w-md">
            <div className="space-y-4">
              <h2 className="text-base font-semibold">Confluências do Termômetro</h2>
              <p className="whitespace-pre-wrap text-sm text-foreground/80">
                {tipText || DEFAULT_THERMOMETER_MEMBER_TIP}
              </p>
              <div className="flex justify-end">
                <Button type="button" onClick={() => setTipModalOpen(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          </Modal>
        </CardContent>
      </Card>
  )
}

function TrendColored({ value }: { value: string }) {
  const v = (value || '').toLowerCase()
  const cls = v === 'bullish' || v === 'alta' ? 'text-emerald-400' : v === 'bearish' || v === 'baixa' ? 'text-red-400' : 'text-foreground/80'
  return <span className={cls}>{value}</span>
}

function ContextoInlineNew({ data }: { data: any }) {
  const cf = (data as any)?.confluence_factors || {}
  const ctx = buildContextParts(data as any)

  const idNum = Number((data as any)?.id)
  const { signals } = useOpportunitySignals(
    Number.isFinite(idNum) ? idNum : null,
    true,
  )

  const invalida = React.useMemo(
    () => {
      const base = data as any
      const stopPrice =
        signals?.stopAggressive ?? signals?.stopConservative ?? signals?.stopPrice ?? base?.stop_price ?? base?.stopPrice ?? null
      if (stopPrice != null && Number.isFinite(Number(stopPrice))) {
        return buildInvalidationText({ ...base, stop_price: Number(stopPrice) })
      }
      return buildInvalidationText(base)
    },
    [data, signals?.stopAggressive, signals?.stopConservative, signals?.stopPrice],
  )

  return (
    <div className="space-y-2 text-sm">
      <div>
        <div className="font-medium">Contexto:</div>
        <div className="text-foreground/80 space-y-1">
          <p>{ctx.header}</p>
          <p>{ctx.emasLine}</p>
          {ctx.fiboLine ? <p>{ctx.fiboLine}</p> : null}
          {ctx.ema200Line ? <p>{ctx.ema200Line}</p> : null}
        </div>
      </div>
      <div>
        <div className="font-medium">Invalidação</div>
        <div className="text-foreground/80">{invalida}</div>
      </div>
    </div>
  )
}
